import {
  AvailabilitySlot,
  CreateReservationInput,
  DEFAULT_RESERVATION_SETTINGS,
  GuestLookup,
  GuestStandingNotes,
  JoinWaitlistInput,
  Reservation,
  ReservationGuest,
  ReservationSettings,
  ReservationTable,
  ServicePeriod,
  ServingHoursState,
  StandingNotesChange,
} from '../models/reservation';
import { timeLabel } from './reservation-time.util';
import {
  ServingHoursDoc,
  ServingHoursGroup,
  bookingRangesForDay,
  exceptionOn,
} from '../utils/serving-hours.util';

/**
 * Wire ⇄ model mapping for `/v1/restaurants/reservations/*` and the
 * `reservation` settings row. The API speaks snake_case; the dashboard
 * models are camelCase. Everything here is pure so it can be unit-tested
 * without HTTP.
 */

// Wire payloads are untyped JSON; the mappers below are the only place that reads them.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Api = any;

/**
 * reservation-service writes `createdAt` / `modifiedAt` as naive UTC
 * ("2026-10-09T15:02:11.123456", no Z), and `new Date()` reads a naive
 * date-time as browser-local. Mark those UTC so "waited" and the stamps are
 * right in every zone; values that already carry an offset pass through.
 */
export function utcIso(value: string): string {
  return value && /T\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/.test(value) ? value + 'Z' : value;
}

export function toReservation(r: Api): Reservation {
  return {
    reservationId: r.reservation_id,
    restaurantId: r.restaurant_id,
    kind: r.kind,
    type: r.type,
    status: r.status,
    statusReason: r.status_reason ?? null,
    channel: r.channel,
    provider: r.provider,
    startAt: utcIso(r.start_at),
    endAt: utcIso(r.end_at),
    serviceDate: r.service_date,
    partySize: Number(r.party_size),
    guest: toGuest(r.guest),
    tableIds: r.table_ids ?? [],
    tableNames: r.table_names ?? [],
    zoneId: r.zone_id ?? null,
    notes: r.notes ?? null,
    occasion: r.occasion ?? null,
    risk: r.risk
      ? { noShowScore: Number(r.risk.no_show_score), modelVersion: r.risk.model_version }
      : null,
    version: Number(r.version ?? 1),
    createdAt: utcIso(r.created_at),
    modifiedAt: utcIso(r.modified_at),
    position: r.position ?? undefined,
    quotedWaitMinutes: r.quoted_wait_minutes ?? undefined,
    notifiedAt: r.notified_at ? utcIso(r.notified_at) : null,
    expiresAt: r.expires_at ? utcIso(r.expires_at) : null,
    smsStatus: r.sms_status ?? null,
    emailStatus: r.email_status ?? null,
  };
}

const optionalNumber = (v: unknown): number | null => (v == null ? null : Number(v));

/** The guest, with their history here when the API sends it (FNF-442). */
export function toGuest(g: Api): ReservationGuest {
  const notes = g?.standing_notes;
  return {
    userId: g?.user_id ?? null,
    name: g?.name ?? '',
    phone: g?.phone ?? null,
    email: g?.email ?? null,
    visits: Number(g?.visits ?? 0),
    noShows: optionalNumber(g?.no_shows),
    lastVisitAt: g?.last_visit_at ? utcIso(g.last_visit_at) : null,
    recentBookings: optionalNumber(g?.recent_bookings),
    recentNoShows: optionalNumber(g?.recent_no_shows),
    bookings: optionalNumber(g?.bookings),
    lastBookingAt: g?.last_booking_at ?? null,
    standingNotes: notes ? toStandingNotes(notes) : null,
    guestKey: g?.guest_key ?? null,
  };
}

function toStandingNotes(n: Api): GuestStandingNotes {
  return {
    allergies: n?.allergies ?? null,
    preferences: n?.preferences ?? null,
    occasions: n?.occasions ?? null,
    tags: n?.tags ?? [],
  };
}

/** `GET …/reservations/guests/lookup` (FNF-442). */
export function toGuestLookup(g: Api): GuestLookup {
  return {
    known: !!g?.known,
    visits: Number(g?.visits ?? 0),
    noShows: Number(g?.no_shows ?? 0),
    lastVisitAt: g?.last_visit_at ? utcIso(g.last_visit_at) : null,
    bookings: Number(g?.bookings ?? 0),
    lastBookingAt: g?.last_booking_at ?? null,
    standingNotes: toStandingNotes(g?.standing_notes),
    guestKey: String(g?.guest_key ?? ''),
    existing: ((g?.existing ?? []) as Api[]).map((e) => ({
      reservationId: e.reservation_id,
      startAt: utcIso(e.start_at),
      endAt: utcIso(e.end_at),
      serviceDate: e.service_date,
      partySize: Number(e.party_size ?? 1),
      status: e.status,
      tableNames: e.table_names ?? [],
      guestName: e.guest_name ?? '',
    })),
  };
}

/** `standing_notes` for a create, join or PATCH body. */
export function fromStandingNotes(change: StandingNotesChange): Api {
  const body: Api = {};
  for (const field of ['allergies', 'preferences', 'occasions', 'tags'] as const) {
    if (change[field] !== undefined) {
      body[field] = change[field];
    }
  }
  return body;
}

/** `timeZone` is the restaurant's, so labels read on its clock. */
export function toSlot(s: Api, timeZone?: string): AvailabilitySlot {
  return {
    startAt: s.start_at,
    label: timeLabel(new Date(s.start_at), timeZone),
    available: !!s.available,
    tableIds: s.table_ids ?? [],
    tableNames: s.table_names ?? [],
    reason: s.reason ?? undefined,
  };
}

export function toTable(t: Api): ReservationTable {
  return {
    id: t.table_id,
    name: t.table_name,
    seats: Number(t.number_of_seats),
    zoneId: typeof t.zone === 'object' && t.zone ? (t.zone.id ?? '') : String(t.zone ?? ''),
  };
}

const DAY_KEYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * Service periods for one date from the restaurant's ServingHours. As in
 * reservation-service (`_periods_from_serving_hours`), the choice is made for
 * the whole week: once any reservation hours are set, only they count, and a
 * day without them takes no bookings. Otherwise store hours apply. Groups
 * with an empty day list apply every day. Named so the book can group by them.
 */
export function toServicePeriods(
  servingHours: Api | null | undefined,
  serviceDate: string,
): ServicePeriod[] {
  const groups: Api[] = servingHours?.serving_hours ?? [];
  // A date exception comes first (FNF-1180): closed, or that date's hours only.
  const exception = exceptionOn(servingHours as ServingHoursDoc, serviceDate);
  if (exception) {
    return namedPeriods(
      exception.kind === 'closed'
        ? []
        : exception.hours
            .filter((h) => h.start_time && h.end_time)
            .map((h) => ({ start: h.start_time, end: h.end_time })),
    );
  }
  const [y, m, d] = serviceDate.split('-').map(Number);
  const day = DAY_KEYS[new Date(y, m - 1, d).getDay()];
  const ofType = (type: string) =>
    groups
      .filter((g) => (g.hour_type ?? 'store_hours') === type)
      .filter((g) => ((g.hours ?? []) as Api[]).some((h) => h.start_time && h.end_time));
  // Booking hours inside the opening hours, as reservation-service offers them (FNF-1515).
  const hours = bookingRangesForDay(
    ofType('reservation_hours') as ServingHoursGroup[],
    ofType('store_hours') as ServingHoursGroup[],
    day,
  ).map((h) => ({ start: h.start_time, end: h.end_time }));
  return namedPeriods(hours);
}

function namedPeriods(hours: { start: string; end: string }[]): ServicePeriod[] {
  const unique = [...new Map(hours.map((h) => [`${h.start}-${h.end}`, h])).values()].sort((a, b) =>
    a.start.localeCompare(b.start),
  );
  return unique.map((h, i) => ({ ...h, name: periodName(h.start, i, unique.length) }));
}

export function toServingHoursState(
  servingHours: Api | null | undefined,
  serviceDate: string,
): ServingHoursState {
  const groups: Api[] = servingHours?.serving_hours ?? [];
  const exception = exceptionOn(servingHours as ServingHoursDoc, serviceDate);
  if (exception?.kind === 'closed') {
    return 'closed_date';
  }
  const hasAnyHours =
    exception?.kind === 'hours' ||
    groups.some((g) => ((g.hours ?? []) as Api[]).some((h) => h.start_time && h.end_time));
  if (!hasAnyHours) {
    return 'unset';
  }
  return toServicePeriods(servingHours, serviceDate).length ? 'open' : 'closed';
}

function periodName(start: string, index: number, total: number): string {
  if (total === 1) {
    return 'All day';
  }
  const hour = Number(start.split(':')[0]);
  if (hour < 11) {
    return 'Breakfast';
  }
  if (hour < 15) {
    return 'Lunch';
  }
  return index === total - 1 ? 'Dinner' : 'Afternoon';
}

export function toSettings(cfg: Api | null | undefined): ReservationSettings {
  if (!cfg) {
    return { ...DEFAULT_RESERVATION_SETTINGS, version: 0 };
  }
  const D = DEFAULT_RESERVATION_SETTINGS;
  return {
    mode: cfg.mode ?? D.mode,
    slotIntervalMinutes: Number(cfg.slot_interval_minutes ?? D.slotIntervalMinutes),
    turnTimes: (cfg.turn_times ?? []).length
      ? cfg.turn_times.map((b: Api) => ({
          maxParty: Number(b.max_party),
          minutes: Number(b.minutes),
        }))
      : D.turnTimes,
    pacing: {
      maxCoversPerInterval: Number(
        cfg.pacing?.max_covers_per_interval ?? D.pacing.maxCoversPerInterval,
      ),
    },
    party: {
      min: Number(cfg.party?.min ?? D.party.min),
      max: Number(cfg.party?.max ?? D.party.max),
      autoConfirmUpTo: Number(cfg.party?.auto_confirm_up_to ?? D.party.autoConfirmUpTo),
    },
    window: {
      minNoticeMinutes: Number(cfg.window?.min_notice_minutes ?? D.window.minNoticeMinutes),
      maxDaysAhead: Number(cfg.window?.max_days_ahead ?? D.window.maxDaysAhead),
    },
    policy: {
      depositCents: Number(cfg.policy?.deposit_cents ?? D.policy.depositCents),
      cancelCutoffMinutes: Number(
        cfg.policy?.cancel_cutoff_minutes ?? D.policy.cancelCutoffMinutes,
      ),
      noShowGraceMinutes: Number(cfg.policy?.no_show_grace_minutes ?? D.policy.noShowGraceMinutes),
    },
    requestExpiryMinutes: Number(cfg.request_expiry_minutes ?? D.requestExpiryMinutes),
    waitlist: {
      quoteStrategy: cfg.waitlist?.quote_strategy ?? D.waitlist.quoteStrategy,
      notifyTimeoutMinutes: Number(
        cfg.waitlist?.notify_timeout_minutes ?? D.waitlist.notifyTimeoutMinutes,
      ),
    },
    channels: {
      google: { enabled: !!cfg.channels?.google?.enabled },
      opentable: {
        enabled: !!cfg.channels?.opentable?.enabled,
        merchantId: cfg.channels?.opentable?.merchant_id ?? null,
      },
    },
    notifications: {
      sms: cfg.notifications?.sms ?? D.notifications.sms,
      email: cfg.notifications?.email ?? D.notifications.email,
      push: cfg.notifications?.push ?? D.notifications.push,
      reminderMinutesBefore: (
        cfg.notifications?.reminder_minutes_before ?? D.notifications.reminderMinutesBefore
      ).map(Number),
    },
    version: Number(cfg.version ?? 0),
  };
}

/** Body for `PUT /v1/restaurants/settings/reservation`. */
export function fromSettings(s: ReservationSettings): Api {
  return {
    mode: s.mode,
    slot_interval_minutes: s.slotIntervalMinutes,
    turn_times: s.turnTimes.map((b) => ({ max_party: b.maxParty, minutes: b.minutes })),
    pacing: { max_covers_per_interval: s.pacing.maxCoversPerInterval },
    party: { min: s.party.min, max: s.party.max, auto_confirm_up_to: s.party.autoConfirmUpTo },
    window: {
      min_notice_minutes: s.window.minNoticeMinutes,
      max_days_ahead: s.window.maxDaysAhead,
    },
    policy: {
      deposit_cents: s.policy.depositCents,
      cancel_cutoff_minutes: s.policy.cancelCutoffMinutes,
      no_show_grace_minutes: s.policy.noShowGraceMinutes,
    },
    request_expiry_minutes: s.requestExpiryMinutes,
    waitlist: {
      quote_strategy: s.waitlist.quoteStrategy,
      notify_timeout_minutes: s.waitlist.notifyTimeoutMinutes,
    },
    channels: {
      google: { enabled: s.channels.google.enabled },
      opentable: {
        enabled: s.channels.opentable.enabled,
        merchant_id: s.channels.opentable.merchantId,
      },
    },
    notifications: {
      sms: s.notifications.sms,
      email: s.notifications.email,
      push: s.notifications.push,
      reminder_minutes_before: s.notifications.reminderMinutesBefore,
    },
    // 0 = no row yet; the service treats a missing version as unconditional.
    ...(s.version > 0 ? { version: s.version } : {}),
  };
}

/** Body for `POST /v1/restaurants/reservations`. */
export function fromCreate(input: CreateReservationInput): Api {
  return {
    start_at: input.startAt,
    party_size: input.partySize,
    guest: fromGuest(input.guest),
    notes: input.notes ?? null,
    occasion: input.occasion ?? null,
    channel: input.channel ?? 'dashboard',
    ...(input.status ? { confirm: input.status === 'confirmed' } : {}),
    ...(input.tableIds?.length ? { table_ids: input.tableIds } : {}),
    ...(input.standingNotes ? { standing_notes: fromStandingNotes(input.standingNotes) } : {}),
  };
}

/** Body for `POST /v1/restaurants/reservations/waitlist`. */
export function fromWaitlistJoin(input: JoinWaitlistInput): Api {
  return {
    party_size: input.partySize,
    guest: fromGuest(input.guest),
    notes: input.notes ?? null,
    channel: 'dashboard',
    ...(input.standingNotes ? { standing_notes: fromStandingNotes(input.standingNotes) } : {}),
  };
}

export function fromGuest(g: Reservation['guest']): Api {
  return {
    name: g.name,
    phone: g.phone ?? null,
    email: g.email ?? null,
    user_id: g.userId ?? null,
  };
}
